import React, { useEffect, useMemo, useState } from "react";
import { listAdminUsers } from "../../api/admin.api";
import Button from "../../ui/Button";
import { Input, Select, Textarea } from "../../ui";
import { colors, typography } from "../../ui/tokens";

const PRIORITY_TO_SEVERITY = {
  Low: "Low",
  Medium: "Medium",
  High: "High",
  Critical: "Critical",
};

const initialValues = {
  title: "",
  description: "",
  owner_id: "",
  assignee_id: "",
  status: "Open",
  priority: "Medium",
  due_date: "",
  source_ref: "",
  evidence_links: "",
};

export default function AuditRecommendationForm({
  auditTitle,
  defaultOwner = "",
  loading = false,
  onSubmit,
}) {
  const [values, setValues] = useState(initialValues);
  const [userOptions, setUserOptions] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");

  useEffect(() => {
    setValues((current) => ({
      ...current,
      owner_id: defaultOwner || current.owner_id,
      assignee_id: defaultOwner || current.assignee_id,
    }));
  }, [defaultOwner]);

  useEffect(() => {
    let active = true;
    const loadUsers = async () => {
      setUsersLoading(true);
      setUsersError("");
      try {
        const res = await listAdminUsers();
        const users = res?.data || [];
        if (!active) return;
        setUserOptions(
          users
            .filter((user) => user?.username)
            .map((user) => ({
              value: user.username,
              label: user.email ? `${user.username} (${user.email})` : user.username,
            }))
        );
      } catch (err) {
        if (!active) return;
        setUserOptions([]);
        setUsersError(err?.response?.data?.detail || err?.message || "Unable to load users.");
      } finally {
        if (active) {
          setUsersLoading(false);
        }
      }
    };
    loadUsers();
    return () => {
      active = false;
    };
  }, []);

  const ownerChoices = useMemo(() => userOptions, [userOptions]);

  const handleChange = (field) => (event) => {
    const nextValue = event?.target?.value ?? "";
    setValues((current) => ({ ...current, [field]: nextValue }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!values.title.trim()) {
      return;
    }
    await onSubmit?.({
      title: values.title.trim(),
      description: values.description.trim() || null,
      severity: PRIORITY_TO_SEVERITY[values.priority] || "Medium",
      status: values.status,
      owner_id: values.owner_id || null,
      assignee_id: values.assignee_id || null,
      due_date: values.due_date || null,
      management_response: null,
      remediation_status: null,
      source_ref: values.source_ref.trim() || null,
      evidence_links: values.evidence_links
        ? values.evidence_links.split(",").map((item) => item.trim()).filter(Boolean)
        : [],
    });
    setValues({
      ...initialValues,
      owner_id: defaultOwner || "",
      assignee_id: defaultOwner || "",
    });
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: "grid", gap: 12 }}>
      <Input
        label="Recommendation title"
        value={values.title}
        onChange={handleChange("title")}
        placeholder="Recommendation title"
        required
        style={{ width: "100%" }}
      />
      <Input
        label="Category"
        value="Internal Audit"
        disabled
        style={{ width: "100%" }}
      />
      <Textarea
        label="Description"
        value={values.description}
        onChange={handleChange("description")}
        placeholder="Description"
        rows={4}
        style={{ width: "100%" }}
      />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        <Select
          label="Owner"
          value={values.owner_id}
          onChange={handleChange("owner_id")}
          disabled={usersLoading || ownerChoices.length === 0}
          style={{ width: "100%" }}
        >
          <option value="">Select owner</option>
          {ownerChoices.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
        <Select
          label="Assignee"
          value={values.assignee_id}
          onChange={handleChange("assignee_id")}
          disabled={usersLoading || ownerChoices.length === 0}
          style={{ width: "100%" }}
        >
          <option value="">Select assignee</option>
          {ownerChoices.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </div>
      {usersError && ownerChoices.length === 0 && (
        <div style={{ color: colors.danger, fontSize: typography.size.xs }}>{usersError}</div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
        <Select
          label="Status"
          value={values.status}
          onChange={handleChange("status")}
          style={{ width: "100%" }}
        >
          {["Open", "In Progress", "Blocked", "Completed"].map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
        <Select
          label="Priority"
          value={values.priority}
          onChange={handleChange("priority")}
          style={{ width: "100%" }}
        >
          {["Low", "Medium", "High", "Critical"].map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        <Input
          label="Due date"
          type="date"
          value={values.due_date}
          onChange={handleChange("due_date")}
          style={{ width: "100%" }}
        />
        <Input
          label="Source reference"
          value={values.source_ref}
          onChange={handleChange("source_ref")}
          placeholder="Source reference"
          style={{ width: "100%" }}
        />
      </div>
      <Input
        label="Evidence links"
        value={values.evidence_links}
        onChange={handleChange("evidence_links")}
        placeholder="Evidence links (comma separated URLs)"
        style={{ width: "100%" }}
      />
      <Input
        label="Parent action"
        value={auditTitle || ""}
        disabled
        style={{ width: "100%" }}
      />
      <div style={{ display: "flex", justifyContent: "flex-start" }}>
        <Button type="submit" disabled={loading} variant="primary">
          {loading ? "Adding..." : "Add Recommendation"}
        </Button>
      </div>
    </form>
  );
}
